import { LeadTableRow } from '@/types/Leads';
import { Box, Column, Table } from '@palmetto/palmetto-components';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import styles from './LeadManagementTable.module.css';

export interface LeadManagementTableProps {
  leads: LeadTableRow[];
  statusDateLabel?: string;
  extraColumns?: Column[];
  renderActions?: (row: LeadTableRow) => React.ReactNode;
}

const formatLocation = (cell?: any) => (
  <Box fontWeight="medium" as="span" display="inline">{`${cell.locality}, ${cell.region} ${cell.postalCode}`}</Box>
);
const formatSystemSize = (cell?: any) => (
  <Box fontWeight="medium" as="span" display="inline">{`${cell.systemSize as number} kW`}</Box>
);
const formatBattery = (cell?: any) => (
  <Box fontWeight="medium" as="span" display="inline">
    {cell != null ? `${cell.capacity as number} kWh` : '—'}
  </Box>
);
const formatPricePerWatt = (cell?: any) => (
  <Box fontWeight="medium" as="span" display="inline">{`$${(cell.ppw as number).toFixed(2)}`}</Box>
);
const formatStatusDate = (cell?: any) => (
  <Box fontWeight="medium" as="span" display="inline">
    {DateTime.fromISO(cell.updatedAt as string).toFormat('MM-dd-yyyy')}
  </Box>
);

const tableScrollable = { x: true } as const;

export const LeadManagementTable = ({
  leads,
  statusDateLabel,
  extraColumns,
  renderActions,
}: LeadManagementTableProps) => {
  const columns: Column[] = useMemo(
    () => [
      { heading: 'Location', dataKey: 'address', render: formatLocation },
      { heading: 'System Size', dataKey: 'metrics', render: formatSystemSize },
      { heading: 'Battery', dataKey: 'storage', render: formatBattery },
      { heading: 'Price Per Watt', dataKey: 'pricing', render: formatPricePerWatt },
      ...(statusDateLabel ? [{ heading: statusDateLabel, dataKey: 'meta', render: formatStatusDate }] : []),
      ...(extraColumns ?? []),
      ...(renderActions
        ? [{ heading: '', dataKey: 'id', render: (_: any, row: any) => renderActions(row as LeadTableRow) }]
        : []),
    ],
    [statusDateLabel, extraColumns, renderActions],
  );

  return (
    <div className={styles.table}>
      <Table rowKey="id" isScrollable={tableScrollable} columns={columns} rows={leads} />
    </div>
  );
};
